import { CreateElementAction, updateScribe as updateScribeAction } from 'js/actionCreators/scribeActions';
import { getCenterOfViewport } from 'js/shared/helpers/element';
import cloneDeep from 'lodash.clonedeep';
import { select, put, SelectEffect, PutEffect } from 'redux-saga/effects';
import { DrawType, RootState, VSImageShapeOrTextElementModel } from 'js/types';
import { AnimationStageSetting, DEFAULT_ENTRANCE_CONFIG } from 'js/reducers/userAnimationSettings';
import {
  ENTRANCE_ANIMATION_THAT_HAVE_DRAW_TYPE,
  ENTRANCE_ANIMATIONS_THAT_ARE_STORED_AS_TWEEN_IN_MODEL,
  ENTRANCE_ANIMATIONS_WITH_CURSOR,
  ENTRANCE_ANIMATIONS_WITH_DIRECTIONS,
  EXIT_ANIMATION_ERASE_KEY,
  EXIT_ANIMATIONS_WITH_DIRECTIONS,
  NO_ANIMATION_KEY,
  SHAKE_LOOP_KEY,
  SPIN_LOOP_KEY
} from 'js/config/consts';
import {
  CLOCKWISE_SPIN_TWEEN,
  COUNTER_CLOCKWISE_SPIN_TWEEN,
  EMPHASIS_TWEEN_DEFAULTS,
  ENTRANCE_TWEEN_DEFAULTS,
  EXIT_TWEEN_DEFAULTS
} from 'js/config/tweens';
import { pauseTime } from 'js/shared/resources/scribedefaults';
import getOptimizelyInstance from 'js/shared/helpers/optimizely';
import { USER_SETTINGS } from 'js/config/featureKeys';

import { findIndexOfLastActiveElement } from '../sagaHelpers/findIndexOfLastActiveElement';
import { getScribeById } from '../selectors';
import { getShakeEmphasisTween } from '../sagaHelpers/getShakeEmphasisTween';

export function buildNewElementWithUserAnimationSettings(
  element: VSImageShapeOrTextElementModel,
  userAnimationStageSettings: AnimationStageSetting
) {
  const optimizely = getOptimizelyInstance();
  if (!optimizely.isFeatureEnabled(USER_SETTINGS)) {
    return element;
  }

  if (element.type === 'SceneTransition') {
    /* 
      This is here because VSImageShapeOrTextElementModel incorrectly includes SceneTransitionElementModel type 
      and removing it would mean having to change a bunch of code in the Pixi advanced timeline to get the correct types. 😡
      There will never be an element of type SceneTransition passed into this function as it only exists in the Pixi timeline. 
    */
    return element;
  }

  const updatedElement = cloneDeep(element);

  const entranceAnimationSettings =
    userAnimationStageSettings.entrance.applyTo === 'individual'
      ? userAnimationStageSettings.entrance.elementTypeSettings[element.type]
      : userAnimationStageSettings.entrance.elementTypeSettings.All;

  if (entranceAnimationSettings) {
    updatedElement.animationTime =
      entranceAnimationSettings.animationType === NO_ANIMATION_KEY ? 0 : entranceAnimationSettings.duration;

    if (ENTRANCE_ANIMATIONS_WITH_CURSOR.includes(entranceAnimationSettings.animationType)) {
      updatedElement.cursorId = entranceAnimationSettings.configuration.cursorId;
    }

    // Different animations are stored in different ways in the model [tweens or animation] (I don't know why) so we need to handle them separately
    if (ENTRANCE_ANIMATIONS_THAT_ARE_STORED_AS_TWEEN_IN_MODEL.includes(entranceAnimationSettings.animationType)) {
      updatedElement.entranceTween = ENTRANCE_TWEEN_DEFAULTS.find(
        tween => tween.id === entranceAnimationSettings.animationType
      );
      if (
        updatedElement.entranceTween?.tweens?.[0] &&
        ENTRANCE_ANIMATIONS_WITH_DIRECTIONS.includes(entranceAnimationSettings.animationType)
      ) {
        updatedElement.entranceTween.tweens[0].position = entranceAnimationSettings.configuration.direction as string;
      }
    } else {
      updatedElement.entranceAnimation = {
        id: entranceAnimationSettings.animationType
      };
    }

    if (ENTRANCE_ANIMATIONS_WITH_DIRECTIONS.includes(entranceAnimationSettings.animationType)) {
      if (ENTRANCE_ANIMATIONS_THAT_ARE_STORED_AS_TWEEN_IN_MODEL.includes(entranceAnimationSettings.animationType)) {
        if (updatedElement.entranceTween?.tweens?.[0]) {
          updatedElement.entranceTween.tweens[0].position = entranceAnimationSettings.configuration.direction as string;
        }
      } else {
        if (updatedElement.entranceAnimation?.config) {
          updatedElement.entranceAnimation.config.direction = entranceAnimationSettings.configuration
            .direction as string;
        }
      }
    }

    if (ENTRANCE_ANIMATION_THAT_HAVE_DRAW_TYPE.includes(entranceAnimationSettings.animationType)) {
      if (updatedElement.entranceAnimation) {
        updatedElement.entranceAnimation.config = {
          ...updatedElement.entranceAnimation.config,
          drawType: entranceAnimationSettings.configuration.drawType as DrawType
        };
      }
    }
  } else {
    // No animation set, so we use the default entrance config
    updatedElement.entranceAnimation = {
      id: DEFAULT_ENTRANCE_CONFIG.animationType
    };
    updatedElement.cursorId = DEFAULT_ENTRANCE_CONFIG.configuration.cursorId;
  }

  const emphasisAnimationSettings =
    userAnimationStageSettings.emphasis.applyTo === 'individual'
      ? userAnimationStageSettings.emphasis.elementTypeSettings[element.type]
      : userAnimationStageSettings.emphasis.elementTypeSettings.All;

  if (emphasisAnimationSettings) {
    updatedElement.emphasisAnimationTime = emphasisAnimationSettings.duration;
    updatedElement.emphasisAnimationLoops = emphasisAnimationSettings.configuration.loops as number;

    switch (emphasisAnimationSettings.animationType) {
      case NO_ANIMATION_KEY: {
        updatedElement.emphasisTween = undefined;
        updatedElement.emphasisAnimationLoops = 0;
        break;
      }
      case SHAKE_LOOP_KEY: {
        updatedElement.emphasisTween = getShakeEmphasisTween(
          emphasisAnimationSettings.configuration.direction as string
        );
        break;
      }
      case SPIN_LOOP_KEY: {
        updatedElement.emphasisTween =
          emphasisAnimationSettings.configuration.spinDirection === 'clockwise'
            ? CLOCKWISE_SPIN_TWEEN
            : COUNTER_CLOCKWISE_SPIN_TWEEN;
        break;
      }
      default: {
        updatedElement.emphasisTween = EMPHASIS_TWEEN_DEFAULTS.find(
          config => config.id === emphasisAnimationSettings.animationType
        );
        break;
      }
    }
  } else {
    updatedElement.emphasisTween = undefined;
    updatedElement.emphasisAnimation = undefined;
    updatedElement.emphasisAnimationTime = 0;
  }

  const exitAnimationSettings =
    userAnimationStageSettings.exit.applyTo === 'individual'
      ? userAnimationStageSettings.exit.elementTypeSettings[element.type]
      : userAnimationStageSettings.exit.elementTypeSettings.All;

  if (exitAnimationSettings && exitAnimationSettings.animationType !== NO_ANIMATION_KEY) {
    updatedElement.exitAnimationTime = exitAnimationSettings.duration;

    switch (exitAnimationSettings.animationType) {
      case EXIT_ANIMATION_ERASE_KEY: {
        updatedElement.exitTween = undefined;
        updatedElement.exitAnimation = {
          id: EXIT_ANIMATION_ERASE_KEY,
          config: {
            cursorId: exitAnimationSettings.configuration.cursorId
          }
        };
        break;
      }
      default: {
        updatedElement.exitTween = EXIT_TWEEN_DEFAULTS.find(
          config => config.id === exitAnimationSettings.animationType
        );
        if (
          EXIT_ANIMATIONS_WITH_DIRECTIONS.includes(exitAnimationSettings.animationType) &&
          updatedElement.exitTween &&
          updatedElement.exitTween.tweens?.[0]
        ) {
          updatedElement.exitTween.tweens[0].position = exitAnimationSettings.configuration.direction as string;
        }
      }
    }
  } else {
    updatedElement.exitAnimation = undefined;
    updatedElement.exitTween = undefined;
    updatedElement.exitAnimationTime = 0;
  }

  const pauseAnimationSettings =
    userAnimationStageSettings.pause.applyTo === 'individual'
      ? userAnimationStageSettings.pause.elementTypeSettings[element.type]
      : userAnimationStageSettings.pause.elementTypeSettings.All;

  if (pauseAnimationSettings) {
    updatedElement.pauseTime = pauseAnimationSettings.duration;
  } else {
    updatedElement.pauseTime = pauseTime;
  }

  return updatedElement;
}

export function* createElement(action: CreateElementAction): Generator<SelectEffect | PutEffect, void, RootState> {
  const { scribeId } = action;

  const state = yield select();
  const activeScribe = getScribeById(state, scribeId);

  if (!activeScribe) {
    console.error(`Scribe with id ${scribeId} not found`);
    return;
  }

  const newActiveScribe = cloneDeep(activeScribe);
  const newElement = buildNewElementWithUserAnimationSettings(
    action.element,
    state.userAnimationSettings.stageSettings
  );
  const activeScene = newActiveScribe.scenes.find(scene => scene.active) || newActiveScribe.scenes[0];

  // Add the elements to the scribe
  newActiveScribe.elements.push(newElement);

  const indexOfLastActiveElement = findIndexOfLastActiveElement(state.scribes.activeElements, activeScene?.elementIds);

  // Add element id to active scene
  if (typeof indexOfLastActiveElement === 'number') {
    activeScene.elementIds.splice(indexOfLastActiveElement + 1, 0, newElement.id);
  } else {
    activeScene.elementIds = [...activeScene.elementIds, newElement.id];
  }
  const { x, y } = getCenterOfViewport(newElement.width * newElement.scaleX, newElement.height * newElement.scaleY);
  newElement.x = x;
  newElement.y = y;
  // Update the scribe
  yield put(updateScribeAction(newActiveScribe, false, [newElement.id]));
}
